import { T, useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import useAccessBadge, {
  LicenseCoarseLevels,
  LicenseFineLevels,
} from '../../../hooks/useAccessBadge';

function getPlanBadgeStyle(
  coarse: LicenseCoarseLevels | undefined,
  fine: LicenseFineLevels | undefined,
): { bg: string; fg: string } {
  if (!coarse || coarse === LicenseCoarseLevels.freemium) {
    return { bg: '#F0ECE5', fg: '#5B5B5A' };
  }
  if (coarse === LicenseCoarseLevels.enterprise && fine === LicenseFineLevels.trial) {
    return { bg: '#FDE7D1', fg: theme.colors.orangeDark };
  }
  if (coarse === LicenseCoarseLevels.enterprise) {
    return { bg: theme.colors.orange, fg: '#FFFFFF' };
  }
  if (coarse === LicenseCoarseLevels.premium) {
    return { bg: theme.colors.purple, fg: '#FFFFFF' };
  }
  return { bg: '#dad5cf', fg: theme.colors.black };
}

const PlanIndicator = ({ isMobileDrawer }: { isMobileDrawer: boolean }) => {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { t } = useTranslate();
  const isReady = userAccessBadge !== undefined;

  const userLicenseLabel = useMemo(() => {
    if (
      !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    ) {
      return t('Free Plan', 'Free Plan');
    } else if (userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium) {
      return t('Premium', 'Premium');
    } else if (userAccessBadge.license.fineLevel === LicenseFineLevels.trial) {
      return (
        '⌛ ' +
        new Intl.DateTimeFormat('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(new Date(userAccessBadge.license.expiresAt ?? 0))
      );
    } else {
      return t('School plan', 'School plan');
    }
  }, [userAccessBadge, t]);

  const schoolNameTooltipText = useMemo(() => {
    return !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium ? (
      userAccessBadge?.organization?.name ?? (
        <T keyName="Personal" defaultValue="Personal" />
      )
    ) : userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium ? (
      <T keyName="Premium" defaultValue="Premium" />
    ) : userAccessBadge.license.fineLevel === LicenseFineLevels.trial ? (
      <T keyName="School Plan Trial" defaultValue="School Plan Trial" />
    ) : (
      userAccessBadge.organization?.name ?? (
        <T keyName="School Plan" defaultValue="School Plan" />
      )
    );
  }, [userAccessBadge]);

  const badgeStyle = getPlanBadgeStyle(
    userAccessBadge?.license?.coarseLevel,
    userAccessBadge?.license?.fineLevel,
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <Tooltip
        title={schoolNameTooltipText}
        mouseEnterDelay={
          typeof schoolNameTooltipText === 'string' && schoolNameTooltipText.length > 20
            ? 1
            : 10000
        }
      >
        <p
          style={{
            fontSize: 16,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            width: '100%',
            marginBottom: 0,
          }}
        >
          {schoolNameTooltipText}
        </p>
      </Tooltip>
      <PointedRow $isMobileDrawer={isMobileDrawer}>
        {isReady && (
          <span
            style={{
              fontSize: 12,
              whiteSpace: 'nowrap',
              background: badgeStyle.bg,
              color: badgeStyle.fg,
              fontWeight: '800',
              padding: '2px 5px',
              borderRadius: 5,
              letterSpacing: '0.06rem',
            }}
          >
            {userLicenseLabel}
          </span>
        )}
      </PointedRow>
    </div>
  );
};

export default PlanIndicator;

const PointedRow = styled.div<{ $isMobileDrawer: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 20px;

  ${({ $isMobileDrawer }) =>
    $isMobileDrawer
      ? css`
          color: ${theme.colors.white};
        `
      : css`
          color: ${theme.colors.greyDark};
        `}
`;
